import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Output,
  EventEmitter,
  computed,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";

const STORAGE_KEY = "enrichment-tour-seen-v1";

interface TourStep {
  /** CSS selector that anchors the tooltip + spotlight. Picks the first
   *  match in the document so it tolerates components that exist multiple
   *  times (e.g. preview-toggle). */
  selector: string;
  title: string;
  body: string;
  /** Preferred placement for the tooltip relative to the spotlight. */
  placement: "top" | "bottom" | "left" | "right";
}

const STEPS: readonly TourStep[] = [
  {
    selector: ".hero__title",
    title: "Your price at a glance",
    body: "This page edits one price. Its status, amount and package live here, with the switcher to its sibling prices and the primary action buttons.",
    placement: "bottom",
  },
  {
    selector: ".hero__readiness",
    title: "Completion guide",
    body: "Tracks how complete your content is. Reach 100% completion for the best customer experience before publishing.",
    placement: "bottom",
  },
  {
    selector: ".hero__lang-grid",
    title: "Language hub",
    body: "Manage content in multiple languages with ease. See which of TH / EN / MM / KH still need your attention and click to switch.",
    placement: "bottom",
  },
  {
    selector: ".stepper__list",
    title: "Workflow steps",
    body: "Walk through the price in order, then the fields shared by the whole package. Each step shows progress — ✓ ready, ◐ in progress, or ⚠ needs review.",
    placement: "bottom",
  },
  {
    selector: ".enrichment-workspace__preview",
    title: "Live mobile preview",
    body: "Watch the customer-facing screen update as you edit. See exactly how your package will look to your customers in real-time.",
    placement: "left",
  },
];

interface Pos {
  top: number;
  left: number;
}

/**
 * Phase 4.5 — Onboarding tour shown on the marketer's first visit. Renders
 * a darkened overlay with a "cutout" spotlight around the current step's
 * anchor element, plus a tooltip card with Next / Skip controls.
 *
 * Self-positioning via getBoundingClientRect() so we don't depend on a
 * tour library. The shell mounts this component conditionally and listens
 * for `done` to flip its visibility flag.
 */
@Component({
  selector: "app-enrichment-edit-package-tour",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./enrichment-edit-package-tour.html",
  styleUrls: ["./enrichment-edit-package-tour.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnrichmentEditPackageTour {
  /** External "tour active" flag — owned by the shell. Use `done`/`skipped`
   *  outputs to react when the user finishes or dismisses. */
  readonly open = input<boolean>(false);

  @Output() done = new EventEmitter<void>();
  @Output() skipped = new EventEmitter<void>();

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  /** Current step index. Defaults to 0; reset when the tour reopens. */
  protected readonly stepIndex = signal(0);

  /** Cached bounding rect of the current step's anchor element, updated on
   *  resize/scroll. `null` when the anchor isn't in the DOM yet. */
  protected readonly anchorRect = signal<DOMRect | null>(null);

  protected readonly currentStep = computed<TourStep | null>(() => {
    if (!this.open()) return null;
    return STEPS[this.stepIndex()] ?? null;
  });

  protected readonly stepCount = STEPS.length;
  protected readonly isLast = computed(
    () => this.stepIndex() === STEPS.length - 1,
  );
  protected readonly isFirst = computed(() => this.stepIndex() === 0);

  protected readonly tooltipPos = computed<Pos | null>(() => {
    const rect = this.anchorRect();
    const step = this.currentStep();
    if (!rect || !step) return null;
    const margin = 16;
    const tooltipW = 320;
    const tooltipH = 180;
    switch (step.placement) {
      case "top":
        return {
          top: Math.max(margin, rect.top - tooltipH - margin),
          left: rect.left + rect.width / 2 - tooltipW / 2,
        };
      case "bottom":
        return {
          top: rect.bottom + margin,
          left: rect.left + rect.width / 2 - tooltipW / 2,
        };
      case "left":
        return {
          top: rect.top + rect.height / 2 - tooltipH / 2,
          left: Math.max(margin, rect.left - tooltipW - margin),
        };
      case "right":
        return {
          top: rect.top + rect.height / 2 - tooltipH / 2,
          left: rect.right + margin,
        };
    }
  });

  /** Spotlight rectangle pushed slightly outward for breathing room. */
  protected readonly spotlightRect = computed(() => {
    const rect = this.anchorRect();
    if (!rect) return null;
    const pad = 8;
    return {
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    };
  });

  constructor() {
    // Whenever the tour opens or the step changes, recompute the anchor rect.
    effect(() => {
      if (!this.open()) {
        this.anchorRect.set(null);
        return;
      }
      // Defer one frame so the target DOM has settled (especially right
      // after the shell mounts).
      void this.stepIndex();
      requestAnimationFrame(() => this.recomputeAnchor());
    });
  }

  // ── Position lifecycle ────────────────────────────────────────────────────
  @HostListener("window:resize")
  @HostListener("window:scroll")
  protected onWindowEvent(): void {
    if (!this.open()) return;
    this.recomputeAnchor();
  }

  private recomputeAnchor(): void {
    const step = STEPS[this.stepIndex()];
    if (!step) return;
    const el = document.querySelector(step.selector) as HTMLElement | null;
    if (!el) {
      this.anchorRect.set(null);
      return;
    }
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
    // After scroll, re-measure on next frame so the rect reflects the
    // post-scroll position.
    requestAnimationFrame(() => {
      this.anchorRect.set(el.getBoundingClientRect());
    });
  }

  // ── User actions ──────────────────────────────────────────────────────────
  next(): void {
    if (this.isLast()) {
      this.finish();
      return;
    }
    this.stepIndex.update((i) => i + 1);
  }

  prev(): void {
    if (this.isFirst()) return;
    this.stepIndex.update((i) => i - 1);
  }

  skip(): void {
    this.markSeen();
    this.skipped.emit();
  }

  finish(): void {
    this.markSeen();
    this.done.emit();
  }

  private markSeen(): void {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // localStorage unavailable (e.g. SSR or privacy mode) — just continue.
    }
  }

  // ── Static helpers used by the shell ─────────────────────────────────────
  static hasSeen(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  }

  static reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  @HostListener("document:keydown", ["$event"])
  protected onKey(event: KeyboardEvent): void {
    if (!this.open()) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.skip();
    } else if (event.key === "ArrowRight" || event.key === "Enter") {
      event.preventDefault();
      this.next();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.prev();
    }
  }
}
